// Remove the Supabase auth session from THIS browser without the network.
//
// supabase.auth.signOut() - even with scope 'local' - first calls the auth
// server and, on a network or retryable error, returns that error and leaves
// the stored session in place (auth-js 2.99 GoTrueClient._signOut). After an
// account deletion, or a sign-out the user asked for while offline, that
// would leave a session in shared storage that a peer tab reloads into. This
// deletes the exact keys the configured client uses (its storageKey, the PKCE
// code verifier, the split-out user), reads storage back to VERIFY the
// session is gone, and only then tells the client's own subscribers
// SIGNED_OUT (every useAuth instance, which then purges and tells peers).
//
// The internals read here (storageKey, storage, userStorage,
// _notifyAllSubscribers) are the ones auth-js itself uses in _removeSession;
// src/lib/__tests__/localSession.test.ts pins them against the installed SDK.
import type { SupabaseClient } from '@supabase/supabase-js'

interface StorageLike {
  getItem(key: string): string | null | Promise<string | null>
  removeItem(key: string): void | Promise<void>
}

interface AuthInternals {
  storageKey?: unknown
  storage?: StorageLike
  userStorage?: StorageLike | null
  _notifyAllSubscribers?: (event: string, session: null) => Promise<void> | void
}

/** The storage key the client keeps its session under, or null. */
export function sessionStorageKey(client: SupabaseClient | null): string | null {
  const key = (client?.auth as unknown as AuthInternals | undefined)?.storageKey
  return typeof key === 'string' && key ? key : null
}

/**
 * The stored session WITHOUT the network (getSession() refreshes an expired
 * token first): its subject and access-token expiry, null when storage holds
 * none, or 'unknown' when the client does not expose its storage. Offline
 * contract C2 (cold start past expiry) and C3 (recovery signs a tombstoned
 * owner out) read it. Never throws.
 */
export async function readStoredSession(client: SupabaseClient | null): Promise<{ subject: string | null; expiresAt: number | null; anonymous: boolean } | null | 'unknown'> {
  if (!client) return null
  const auth = client.auth as unknown as AuthInternals
  const key = sessionStorageKey(client)
  const storage = auth.storage
  // eslint-disable-next-line i18next/no-literal-string -- a result code
  if (!key || !storage || typeof storage.getItem !== 'function') return 'unknown'
  let raw: string | null
  try {
    raw = await storage.getItem(key)
  } catch {
    // A read that failed proves nothing about absence (r2 #12).
    // eslint-disable-next-line i18next/no-literal-string -- a result code
    return 'unknown'
  }
  try {
    if (!raw) return null
    const s = JSON.parse(raw) as { user?: { id?: unknown; is_anonymous?: unknown } | null; expires_at?: unknown; refresh_token?: unknown }
    let user = s.user
    if (!user && auth.userStorage) {
      // eslint-disable-next-line i18next/no-literal-string -- storage key suffix, as auth-js writes it
      const u = await auth.userStorage.getItem(`${key}-user`)
      if (u) user = (JSON.parse(u) as { user?: typeof user }).user ?? null
    }
    if (typeof s.refresh_token !== 'string' || !s.refresh_token) return null
    return {
      subject: typeof user?.id === 'string' ? user.id : null,
      expiresAt: typeof s.expires_at === 'number' ? s.expires_at : null,
      anonymous: user?.is_anonymous === true,
    }
  } catch {
    return null
  }
}

/**
 * The stored session's subject and refresh token, read SYNCHRONOUSLY (the
 * offline runtime binds a refresh answer to the token it refused; see
 * src/lib/offline/refreshObserver.ts). null: none stored; 'unknown': the
 * storage is async, missing or failed. Never throws.
 */
export function readStoredRefreshSync(client: SupabaseClient | null): { subject: string | null; refreshToken: string | null } | null | 'unknown' {
  if (!client) return null
  const auth = client.auth as unknown as AuthInternals
  const key = sessionStorageKey(client)
  const storage = auth.storage
  // eslint-disable-next-line i18next/no-literal-string -- a result code
  if (!key || !storage || typeof storage.getItem !== 'function') return 'unknown'
  let raw: unknown
  try {
    raw = storage.getItem(key)
  } catch {
    // eslint-disable-next-line i18next/no-literal-string -- a result code
    return 'unknown'
  }
  // eslint-disable-next-line i18next/no-literal-string -- a result code
  if (raw && typeof raw === 'object' && typeof (raw as Promise<unknown>).then === 'function') return 'unknown'
  if (!raw || typeof raw !== 'string') return null
  try {
    const s = JSON.parse(raw) as { user?: { id?: unknown } | null; refresh_token?: unknown }
    return {
      subject: typeof s.user?.id === 'string' ? s.user.id.toLowerCase() : null,
      refreshToken: typeof s.refresh_token === 'string' && s.refresh_token ? s.refresh_token : null,
    }
  } catch {
    return null
  }
}

/**
 * Delete the client's stored session and verify it is gone. Resolves
 * 'removed' only when storage no longer holds it (then subscribers hear
 * SIGNED_OUT), 'present' when it could not be removed, and 'unknown' when the
 * client does not expose auth-js's storage internals (a test double; the
 * installed SDK always does, pinned by localSession.test.ts). Never throws.
 */
export type LocalSessionRemoval = 'removed' | 'present' | 'unknown'

export async function removeLocalSession(client: SupabaseClient | null): Promise<LocalSessionRemoval> {
  // eslint-disable-next-line i18next/no-literal-string -- a result code
  if (!client) return 'removed'
  const auth = client.auth as unknown as AuthInternals
  const key = sessionStorageKey(client)
  const storage = auth.storage
  // eslint-disable-next-line i18next/no-literal-string -- a result code
  if (!key || !storage || typeof storage.getItem !== 'function' || typeof storage.removeItem !== 'function') return 'unknown'
  // eslint-disable-next-line i18next/no-literal-string -- storage key suffixes, as auth-js writes them
  const keys = [key, `${key}-code-verifier`, `${key}-user`]
  for (const k of keys) {
    try { await storage.removeItem(k) } catch { /* verified below */ }
  }
  if (auth.userStorage) {
    // eslint-disable-next-line i18next/no-literal-string -- storage key suffix, as auth-js writes it
    try { await auth.userStorage.removeItem(`${key}-user`) } catch { /* not the session itself */ }
  }
  let gone = false
  try {
    gone = (await storage.getItem(key)) == null
  } catch {
    gone = false
  }
  // eslint-disable-next-line i18next/no-literal-string -- a result code
  if (!gone) return 'present'
  try {
    // eslint-disable-next-line i18next/no-literal-string -- an auth event name
    await auth._notifyAllSubscribers?.('SIGNED_OUT', null)
  } catch {
    // The session is gone either way; the callers apply signed-out state too.
  }
  // eslint-disable-next-line i18next/no-literal-string -- a result code
  return 'removed'
}
